import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authService } from '../services/auth';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    token: '',
    password: '',
    password_confirmation: '',
  });

  useEffect(() => {
    // Leer token y username desde query params
    const token = searchParams.get('token');
    const username = searchParams.get('username');

    if (token && username) {
      setFormData(prev => ({
        ...prev,
        token: token,
        username: decodeURIComponent(username),
      }));
    } else {
      // Si no hay token o username, redirigir a forgot-password
      toast({
        title: "Enlace inválido",
        description: "El enlace de recuperación no es válido.",
        variant: "destructive",
      });
      navigate('/forgot-password');
    }
  }, [searchParams, navigate]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Validaciones básicas
    if (!formData.password || formData.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.password_confirmation) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    try {
      const response = await authService.resetPassword({
        username: formData.username,
        token: formData.token,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
      });

      if (response.success) {
        setSuccess(true);
        toast({
          title: "¡Contraseña restablecida!",
          description: "Tu contraseña ha sido restablecida exitosamente.",
        });
        
        // Redirigir al login después de 2 segundos
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      const errorMessage = err.message || 'Error al restablecer la contraseña. Verifica que el enlace no haya expirado.';
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-black px-6 py-8">
        <div className="max-w-md mx-auto">
          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">¡Contraseña restablecida!</h1>
              <p className="text-[#A2A2A2]">Tu contraseña ha sido restablecida exitosamente.</p>
            </div>
            <div className="bg-green-500/10 border border-green-500/50 rounded-2xl p-4">
              <p className="text-green-400 text-sm">
                Serás redirigido al login en unos momentos...
              </p>
            </div>
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-white text-black hover:bg-white/90 rounded-full py-6 text-lg font-medium transition-all hover:scale-105 active:scale-95"
            >
              Ir al login
            </Button>
          </div>
        </div>
        <Toaster />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black px-6 py-8">
      <div className="max-w-md mx-auto">
        {/* Back button */}
        <button 
          onClick={() => navigate('/login')}
          className="flex items-center gap-2 text-white hover:text-[#A2A2A2] transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Volver</span>
        </button>

        <div className="space-y-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Restablecer contraseña</h1>
            <p className="text-[#A2A2A2]">Ingresa tu nueva contraseña</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-white">Usuario</Label>
              <Input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleInputChange}
                className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                required
                disabled
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">Nueva contraseña</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                required
                minLength={8}
                autoFocus
              />
              <p className="text-[#A2A2A2] text-xs">Mínimo 8 caracteres</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password_confirmation" className="text-white">Confirmar contraseña</Label>
              <Input
                id="password_confirmation"
                name="password_confirmation"
                type="password"
                value={formData.password_confirmation}
                onChange={handleInputChange}
                className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                required
                minLength={8}
              />
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-2xl p-4">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !formData.token || !formData.username}
              className="w-full bg-white text-black hover:bg-white/90 rounded-full py-6 text-lg font-medium transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Restableciendo...' : 'Restablecer contraseña'}
            </Button>
          </form>
        </div>
      </div>
      <Toaster />
    </div>
  );
};

export default ResetPassword;

